// Route catalog — built from the actual Next.js /src/app directory structure.
// Route groups like (dashboard), (auth) are stripped from the URL automatically by Next.js.
// Dynamic segments [id] are shown as {param_name} for the AI.
//
// TAB NAVIGATION RULES:
//   Pages whose tab IS a URL param (integrations, leads, customers):
//     → Use "navigation" event: { "type": "navigation", "path": "/integrations", "query": { "tab": "calls" } }
//   Pages whose tab is LOCAL STATE only (command agent, sales agent, support agent, users, settings, billing, help, documents):
//     → Navigate to the page first, then use "open_tab" event: { "type": "open_tab", "tab": "settings" }
//     → OR combine: navigation to page + open_tab in same response stream
//
// API COMMENTS on detail routes describe the backend endpoints + accepted fields for that resource.

export interface RouteEntry {
  path: string;          // URL pattern — {param} = dynamic segment
  name: string;
  params?: string[];     // required path params
  tabs?: string[];       // valid tab values
  query?: Record<string, string>; // accepted query params → description
}

export const ROUTE_CATALOG: RouteEntry[] = [
  // ── Dashboard ─────────────────────────────────────────────────────────────
  { path: "/teams",           name: "Teams Overview" },
  { path: "/activity-log",    name: "Activity Log" },
  { path: "/usage",           name: "Usage" },
  { path: "/scheduled-job",   name: "Scheduled Jobs" },

  // ── CRM ───────────────────────────────────────────────────────────────────
  { path: "/crm",             name: "CRM / Persons List" },
  { path: "/crm/{id}",        name: "Person Detail",     params: ["id"],
    query: {
      name:        "Display name shown in breadcrumb",
      interaction: "Open a specific interaction by ID",
      type:        "Interaction type e.g. CALL_PREVIEW | WHATSAPP | EMAIL",
    }},

  // ── Users & Roles ─────────────────────────────────────────────────────────
  { path: "/users",           name: "Users & Roles",     tabs: ["users", "roles"] },

  // ── Documents ─────────────────────────────────────────────────────────────
  { path: "/documents",       name: "Documents",         tabs: ["datasource", "documents"] },



  // ── Integrations ──────────────────────────────────────────────────────────
  { path: "/integrations",               name: "Integrations",   tabs: ["apps", "calls", "triggers", "custom-app"],
    query: { tab: "Active tab: apps | calls | triggers | custom-app" }},
  { path: "/integrations/custom-app",    name: "Custom Apps" },
  { path: "/integrations/custom-app/{id}", name: "Custom App Detail", params: ["id"] },

  // ── Blueprints ────────────────────────────────────────────────────────────
  { path: "/blueprints",      name: "Blueprints" },

  // ── Billing ───────────────────────────────────────────────────────────────
  { path: "/billing",         name: "Billing",           tabs: ["billing", "credits"] },

  // ── Settings ──────────────────────────────────────────────────────────────
  { path: "/settings",        name: "Settings",          tabs: ["organization", "user", "security", "webhook"] },

  // ── Help ──────────────────────────────────────────────────────────────────
  { path: "/help",            name: "Help",              tabs: ["documentation", "support"] },

  // ── Sales Team ────────────────────────────────────────────────────────────
  { path: "/teams/sales-team/leads",                    name: "Leads List",
    query: { page: "Page number", tab: "Active tab" }},
  { path: "/teams/sales-team/leads/{id}",               name: "Lead Detail",          params: ["id"],
    query: { name: "Lead display name (breadcrumb)", interaction: "Open interaction by ID", type: "Interaction type e.g. CALL_PREVIEW" }},
  { path: "/teams/sales-team/sales-agents",             name: "Sales Agents List" },
  { path: "/teams/sales-team/sales-agents/{id}",        name: "Sales Agent Detail",   params: ["id"], tabs: ["settings", "tools", "knowledge-base", "channels"],
    query: { name: "Agent display name (breadcrumb)" }},
  { path: "/teams/sales-team/sales-personas",           name: "Sales Personas List" },
  { path: "/teams/sales-team/sales-personas/{id}",      name: "Sales Persona Detail", params: ["id"] },
  { path: "/teams/sales-team/sales-activity-log",       name: "Sales Activity Log" },

  // ── Support Team ──────────────────────────────────────────────────────────
  { path: "/teams/support-team/customers",              name: "Customers List",
    query: { page: "Page number", tab: "Active tab" }},
  { path: "/teams/support-team/customers/{id}",         name: "Customer Detail",      params: ["id"],
    query: { name: "Customer display name (breadcrumb)", interaction: "Open interaction by ID", type: "Interaction type e.g. CALL_PREVIEW" }},
  { path: "/teams/support-team/support-agents",         name: "Support Agents List" },
  { path: "/teams/support-team/support-agents/{id}",    name: "Support Agent Detail", params: ["id"], tabs: ["settings", "tools", "knowledge-base", "channels"],
    query: { name: "Agent display name (breadcrumb)" }},
  { path: "/teams/support-team/support-personas",       name: "Support Personas List" },
  { path: "/teams/support-team/support-personas/{id}",  name: "Support Persona Detail", params: ["id"] },
  { path: "/teams/support-team/support-activity-log",   name: "Support Activity Log" },

  // ── Analyst Team ──────────────────────────────────────────────────────────
  { path: "/teams/analyst-team/analyst-agent",                    name: "Analyst Agents List" },
  { path: "/teams/analyst-team/analyst-agent/{id}",               name: "Analyst Agent Detail", params: ["id"], tabs: ["analysis", "settings", "views"] },
  { path: "/teams/analyst-team/analyst-agent/{id}/analysis/{analysisId}", name: "Analysis Detail", params: ["id", "analysisId"] },
  { path: "/teams/analyst-team/analyst-view/{id}",                name: "Analyst View",         params: ["id"] },
  { path: "/teams/analyst-team/analyst-file-manager",             name: "Analyst File Manager" },
  { path: "/teams/analyst-team/analyst-file-manager/{id}",        name: "Analyst File Manager Detail", params: ["id"] },
  { path: "/teams/analyst-team/analyst-activity-log",             name: "Analyst Activity Log" },

  // ── Computer Use Team ─────────────────────────────────────────────────────
  { path: "/teams/computer-use-team/computer-use-agent",           name: "Computer Use Agents List" },
  { path: "/teams/computer-use-team/computer-use-agent/{id}",      name: "Computer Use Agent Detail", params: ["id"], tabs: ["agents-runs", "settings", "knowledge-base"] },
  { path: "/teams/computer-use-team/computer-use-agent/{id}/details/{run_id}", name: "Run Detail", params: ["id", "run_id"] },
  { path: "/teams/computer-use-team/computer-use-runs",            name: "Computer Use Runs" },
  { path: "/teams/computer-use-team/computer-use-runs/{id}",       name: "Computer Use Run Detail", params: ["id"] },
  { path: "/teams/computer-use-team/computer-use-activity-logs",   name: "Computer Use Activity Logs" },

  // ── Command Team ──────────────────────────────────────────────────────────
  // Agents list — GET /api/command-team/agents?search=&page=&sort_by=&sort_order=
  { path: "/teams/command-team/agents",            name: "Command Agents List",
    query: { page: "Page number (default 1)", search: "Search by agent name" }},

  // Agent detail — tabs use open_tab action (local state, NOT URL param)
  // Update:   PATCH /api/command-team/agents/{agent_id}  body: { name, description, role, persona, restrictions, llm_provider, llm_model, llm_timeout, llm_temperature, llm_max_tokens, is_public, shared_roles, app_integrations, custom_apps, datasources }
  // Activate: PATCH /api/command-team/agents/{agent_id}/activate  (no body)
  // Delete:   DELETE /api/command-team/agents/{agent_id}
  { path: "/teams/command-team/agents/{agent_id}", name: "Command Agent Detail", params: ["agent_id"],
    tabs: ["settings", "knowledge-base", "scheduled-jobs"],
    query: { name: "Agent display name (breadcrumb)" }},

  // Chat — sessions list: GET /api/command-team/agents/{agent_id}/sessions?page=&limit=10&sort_by=updated_at&sort_order=desc&search=
  // Create session: POST /api/command-team/agents/{agent_id}/sessions  (no body)
  // Update session: PATCH /api/command-team/agents/{agent_id}/sessions/{session_id}  body: { pinned?: boolean, title?: string }
  // Delete session: DELETE /api/command-team/agents/{agent_id}/sessions/{session_id}
  // Messages:       GET /api/command-team/agents/{agent_id}/sessions/{session_id}/messages?page=&limit=30&sort_by=created_at&sort_order=desc
  { path: "/teams/command-team/chat",              name: "Command Chat" },
  { path: "/teams/command-team/chat/{agent_id}",   name: "Command Agent Chat", params: ["agent_id"],
    query: { session_id: "Resume a specific existing session by ID", name: "Agent display name (breadcrumb)" }},

  { path: "/teams/command-team/prompts",           name: "Command Prompts" },
  { path: "/teams/command-team/file-manager",      name: "Command File Manager",
    query: { page: "Page number", session_id: "Filter files by session" }},
  { path: "/teams/command-team/activity-logs",     name: "Command Activity Logs",
    query: { page: "Page number" }},

  // ── Tasks Team ────────────────────────────────────────────────────────────
  { path: "/teams/tasks-team/task-agents",            name: "Task Agents List" },
  { path: "/teams/tasks-team/task-agents/{id}",       name: "Task Agent Detail",    params: ["id"] },
  { path: "/teams/tasks-team/tasks-runs",             name: "Task Runs" },
  { path: "/teams/tasks-team/tasks-runs/{id}",        name: "Task Run Detail",      params: ["id"] },
  { path: "/teams/tasks-team/tasks-activity-log",     name: "Tasks Activity Log" },
];

export function serializeCatalog() {
  return ROUTE_CATALOG.map((r) => ({
    path: r.path,
    name: r.name,
    ...(r.params?.length       ? { params: r.params }           : {}),
    ...(r.tabs?.length         ? { tabs: r.tabs }               : {}),
    ...(r.query && Object.keys(r.query).length ? { query: r.query } : {}),
  }));
}
